// Top-level orchestrator. Owns every singleton that scenes consume so scenes
// don't need to know about construction.

import { AssetLoader } from "../engine/render/AssetLoader";
import { InputManager } from "../engine/input/InputManager";
import { Renderer } from "../engine/render/Renderer";
import { SceneManager } from "../engine/scene/SceneManager";
import { RNG, randomSeed } from "./RNG";
import { RunState, saveRun, clearSave, loadSavedRun, hasSavedRun } from "./RunState";
import { AchievementStore } from "./achievements";
import { AchievementTracker } from "./AchievementTracker";
import type { Difficulty } from "../data/difficulty";

export class Game {
  readonly renderer: Renderer;
  readonly input: InputManager;
  readonly assets: AssetLoader;
  readonly sceneManager: SceneManager;
  readonly achievements: AchievementTracker;
  runState: RunState | null = null;
  rng: RNG;
  readonly hudRoot: HTMLElement;
  readonly sceneRoot: HTMLElement;
  // ?debug=true unlocks all champions in the selection screen
  readonly debug: boolean = new URLSearchParams(window.location.search).get("debug") === "true";
  // ?cheat=true sets all roster units to level 18
  readonly cheat: boolean = new URLSearchParams(window.location.search).get("cheat") === "true";

  private lastTime = performance.now();
  private rafId = 0;
  private onResize: () => void;

  constructor(
    canvas: HTMLCanvasElement,
    sceneRoot: HTMLElement,
    hudRoot: HTMLElement,
  ) {
    this.renderer = new Renderer(canvas);
    this.input = new InputManager(canvas);
    this.assets = new AssetLoader();
    this.sceneManager = new SceneManager();
    this.achievements = new AchievementTracker(new AchievementStore());
    this.rng = new RNG(randomSeed());
    this.sceneRoot = sceneRoot;
    this.hudRoot = hudRoot;

    this.onResize = () => this.renderer.resize();
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
    window.addEventListener("beforeunload", () => { if (this.runState) saveRun(this.runState); });
  }

  startRun(seed?: number, difficulty: Difficulty = "normal"): void {
    const s = seed ?? randomSeed();
    this.rng = new RNG(s);
    this.runState = new RunState(s, this.cheat, difficulty); // champion picked in ChampionSelectScene
    this.achievements.onRunStart();
  }

  saveRun(): void {
    if (this.runState) saveRun(this.runState);
  }

  clearSave(): void {
    clearSave();
  }

  loadSavedRun(): RunState | null {
    return loadSavedRun();
  }

  hasSavedRun(): boolean {
    return hasSavedRun();
  }

  run(): void {
    const loop = (now: number) => {
      const dt = Math.min(64, now - this.lastTime);
      this.lastTime = now;
      // Always re-measure in case a layout change resized the canvas without a
      // window resize event (e.g. DOM sibling being added/removed).
      this.renderer.resize();
      this.sceneManager.update(dt);
      this.renderer.clear("#0a0e14");
      this.sceneManager.render(this.renderer.ctx);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
  }
}
